import { useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'sas-quote-costing-panel-widths';
const defaults = { tree: 210, detail: 360, designation: 400 };
const minimum = { tree: 170, detail: 320, designation: 180 };
const VISIBILITY_KEY = 'sas-quote-costing-panel-visibility';

export default function useCostingPanelResize() {
  const workspaceRef = useRef(null);
  const detailRef = useRef(null);
  const drag = useRef(null);
  const [width, setWidth] = useState(0);
  const [visibleTop, setVisibleTop] = useState(0);
  const [detailHeight, setDetailHeight] = useState(0);
  const [detailHeaderHeight, setDetailHeaderHeight] = useState(48);
  const isDrawer = width > 0 && width < 850;
  const [collapsed, setCollapsed] = useState(() => {
    try { const saved = JSON.parse(localStorage.getItem(VISIBILITY_KEY)); return { tree: saved?.tree === true, detail: saved?.detail === true }; }
    catch { return { tree: false, detail: false }; }
  });
  const [sizes, setSizes] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      return Object.fromEntries(Object.keys(defaults).map((key) => [key,
        Number.isFinite(saved?.[key]) ? Math.max(minimum[key], saved[key]) : defaults[key],
      ]));
    } catch { return defaults; }
  });
  useEffect(() => {
    let frame;
    const measureTop = () => {
      frame = null;
      if (workspaceRef.current) setVisibleTop(Math.max(0, Math.ceil(workspaceRef.current.getBoundingClientRect().top)));
    };
    const schedule = () => { if (frame == null) frame = requestAnimationFrame(measureTop); };
    const observer = new ResizeObserver(([entry]) => { setWidth(entry.contentRect.width); schedule(); });
    observer.observe(workspaceRef.current);
    measureTop();
    window.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule);
    return () => { observer.disconnect(); window.removeEventListener('scroll', schedule, true); window.removeEventListener('resize', schedule); if (frame != null) cancelAnimationFrame(frame); };
  }, []);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(sizes)); } catch { /* Storage may be unavailable. */ }
  }, [sizes]);
  useEffect(() => {
    const panel = detailRef.current;
    if (!panel) return undefined;
    const measure = () => {
      setDetailHeight(Math.ceil(panel.getBoundingClientRect().height));
      setDetailHeaderHeight(Math.ceil(panel.querySelector('header')?.getBoundingClientRect().height || 48));
    };
    const observer = new ResizeObserver(measure);
    observer.observe(panel);
    measure();
    return () => observer.disconnect();
  }, [isDrawer]);
  useEffect(() => {
    try { localStorage.setItem(VISIBILITY_KEY, JSON.stringify(collapsed)); } catch { /* Optional preference storage. */ }
  }, [collapsed]);

  // Use the actual workspace width, after the ERP sidebar and page padding.
  // Reserve the main table and use overlays when all three panels cannot fit.
  const layout = isDrawer ? 'drawer' : width > 1180 ? 'wide' : 'side';
  const tree = Math.min(sizes.tree, Math.max(minimum.tree, width - 480 - (collapsed.detail ? 36 : minimum.detail)));
  const treeSpace = layout === 'wide' && !collapsed.tree ? tree : 36;
  const detail = Math.min(sizes.detail, Math.max(minimum.detail, width - 480 - treeSpace));
  const designationMaximum = Math.min(1000, Math.max(minimum.designation,
    width ? width - treeSpace - (layout === 'drawer' ? 0 : collapsed.detail ? 36 : detail) - 80 : 1000));
  const visible = { tree, detail, designation: Math.min(sizes.designation, designationMaximum) };
  const maximum = panel => panel === 'designation' ? designationMaximum
    : Math.max(minimum[panel], width - 480 - (panel === 'tree' ? (collapsed.detail ? 36 : detail) : treeSpace));
  const change = (panel, value) => {
    setSizes(current => ({ ...current, [panel]: Math.round(Math.max(minimum[panel], Math.min(maximum(panel), value))) }));
  };
  const reset = panel => setSizes(current => panel === 'designation'
    ? { ...current, designation: defaults.designation } : { ...defaults, designation: current.designation });
  const separatorProps = (panel) => ({
    role: 'separator',
    tabIndex: 0,
    'aria-orientation': 'vertical',
    'aria-label': panel === 'designation' ? 'Largeur de la colonne Désignation' : panel === 'tree' ? 'Largeur de l’arborescence' : 'Largeur du détail de ligne',
    'aria-valuemin': minimum[panel],
    'aria-valuemax': Math.round(maximum(panel)),
    'aria-valuenow': Math.round(visible[panel]),
    'aria-valuetext': `${Math.round(visible[panel])} pixels`,
    title: 'Glisser pour redimensionner · Flèches pour ajuster · Double-clic pour réinitialiser',
    onPointerDown: (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.currentTarget.focus();
      event.currentTarget.setPointerCapture(event.pointerId);
      drag.current = { panel, x: event.clientX, size: visible[panel] };
    },
    onPointerMove: (event) => {
      if (drag.current?.panel !== panel) return;
      change(panel, drag.current.size + (event.clientX - drag.current.x) * (panel === 'detail' ? -1 : 1));
    },
    onPointerUp: (event) => {
      drag.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    },
    onPointerCancel: () => { drag.current = null; },
    onLostPointerCapture: () => { drag.current = null; },
    onDoubleClick: () => reset(panel),
    onKeyDown: (event) => {
      if (event.key === 'Enter') { event.preventDefault(); reset(panel); return; }
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const step = event.shiftKey ? 50 : 10;
      const delta = (event.key === 'ArrowRight' ? step : -step) * (panel === 'detail' ? -1 : 1);
      change(panel, event.key === 'Home' ? minimum[panel] : event.key === 'End' ? maximum(panel) : visible[panel] + delta);
    },
  });
  return { workspaceRef, detailRef, separatorProps, layout, collapsed, togglePanel: panel => setCollapsed(current => ({ ...current, [panel]: !current[panel] })),
    style: { '--quote-tree-width': `${layout === 'wide' && !collapsed.tree ? tree : 36}px`, '--quote-detail-width': `${collapsed.detail ? 36 : detail}px`, '--quote-designation-width': `${visible.designation}px`, '--quote-workspace-visible-top': `${visibleTop}px`, '--quote-detail-height': `${detailHeight}px`, '--quote-detail-header-height': `${detailHeaderHeight}px` } };
}
